import { useState } from 'react';
import * as XLSX from 'xlsx';
import { adminService } from '../../../services/adminService';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage import/export operations for Users page
 * @param {Function} fetchUsers - Function to refetch users after import
 * @returns {Object} Import/export state and handlers
 */
export const useUsersImportExport = (fetchUsers) => {
  const { showSnackbar } = useSnackbar();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const downloadCSV = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToExcel = (exportData) => {
    // Convert export data to array format for XLSX
    if (!exportData || !exportData.users || exportData.users.length === 0) {
      return;
    }

    const headers = Object.keys(exportData.users[0]);
    const rows = exportData.users.map((user) =>
      headers.map((header) => user[header] || '')
    );

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 25 }, // Name
      { wch: 35 }, // Email
      { wch: 15 }, // Role
      { wch: 20 }, // Designation
      { wch: 15 }, // Region
      { wch: 20 }, // Department
      { wch: 18 }, // WhatsApp Number
      { wch: 15 }, // Can Be Assignee
      { wch: 15 }, // Can Be Assignor
      { wch: 15 }, // Escalation Level
      { wch: 12 }, // Status
      { wch: 15 }, // Created Date
      { wch: 15 }, // Updated Date
      { wch: 30 }, // Projects
    ];

    XLSX.writeFile(workbook, `users_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      if (format === 'csv') {
        // Get CSV blob from API
        const blob = await adminService.exportUsers('csv');
        downloadCSV(blob);
        showSnackbar('Users exported to CSV successfully', 'success');
        return true;
      } else if (format === 'excel') {
        // Get Excel data from API and convert using XLSX
        // API returns: { success: true, message: "...", data: { users: [...], total: number, format: "excel" } }
        const exportData = await adminService.exportUsers('excel');
        if (!exportData || !exportData.users || exportData.users.length === 0) {
          showSnackbar('No users to export', 'info');
          return false;
        }
        exportToExcel(exportData);
        showSnackbar('Users exported to Excel successfully', 'success');
        return true;
      }
    } catch (error) {
      // console.error('Failed to export users:', error);
      
      // Handle different error types according to API documentation
      let message = 'Failed to export users';
      
      if (error.response) {
        const status = error.response.status;
        const errorResponse = error.response.data;
        
        if (status === 404) {
          // No users found
          message = errorResponse.message || 'No users match the export criteria';
          showSnackbar(message, 'info');
        } else if (status === 400) {
          // Invalid format
          message = errorResponse.message || errorResponse.error || 'Invalid export format';
          showSnackbar(message, 'error');
        } else if (status === 500) {
          // Server error
          message = errorResponse.message || 'Server error occurred during export';
          showSnackbar(message, 'error');
        } else {
          message = errorResponse.message || errorResponse.error || message;
          showSnackbar(message, 'error');
        }
      } else {
        // Network or other error
        message = error.message || message;
        showSnackbar(message, 'error');
      }
      return false;
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (file) => {
    setImporting(true);
    setImportResults(null);
    try {
      // Validate file type
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!['.csv', '.xlsx', '.xls'].includes(fileExtension)) {
        showSnackbar('Invalid file type. Please select a CSV or Excel file.', 'error');
        setImporting(false);
        return;
      }

      // Validate file size (25MB limit)
      if (file.size > 25 * 1024 * 1024) {
        showSnackbar('File size must be less than 25MB', 'error');
        setImporting(false);
        return;
      }

      const result = await adminService.importUsers(file);
      
      // API response format: { success: true, message: "...", data: { total, success: [...], errors: [...] } }
      if (result && result.success) {
        setImportResults(result);
        
        const successCount = result.data?.success?.length || 0;
        const errorCount = result.data?.errors?.length || 0;
        
        if (successCount > 0) {
          showSnackbar(result.message || `Successfully imported ${successCount} user(s)`, 'success');
          fetchUsers(); // Refresh the user list
        } else if (errorCount > 0) {
          showSnackbar(result.message || 'No users were imported. Please check the errors.', 'warning');
        } else {
          showSnackbar('No users were imported. Please check the file format.', 'warning');
        }
      } else {
        // Handle unexpected response format
        showSnackbar('Unexpected response from server', 'error');
      }
    } catch (error) {
      // console.error('Failed to import users:', error);
      
      // Handle different error types according to API documentation
      let message = 'Failed to import users';
      let errorData = {
        success: false,
        message: message,
        data: {
          total: 0,
          success: [],
          errors: []
        }
      };

      if (error.response) {
        const status = error.response.status;
        const errorResponse = error.response.data;
        
        if (status === 400) {
          // Validation error (no file, empty file, Excel not supported, etc.)
          message = errorResponse.message || errorResponse.error || 'Invalid file format';
          errorData.message = message;
          errorData.data.errors = [{ row: 0, email: 'N/A', error: message }];
        } else if (status === 500) {
          // Server error
          message = errorResponse.message || 'Server error occurred during import';
          errorData.message = message;
          errorData.data.errors = [{ row: 0, email: 'N/A', error: message }];
        } else {
          message = errorResponse.message || errorResponse.error || message;
          errorData.message = message;
          errorData.data.errors = [{ row: 0, email: 'N/A', error: message }];
        }
      } else if (error.message) {
        // Network or other error
        message = error.message;
        errorData.message = message;
        errorData.data.errors = [{ row: 0, email: 'N/A', error: message }];
      }
      
      showSnackbar(message, 'error');
      setImportResults(errorData);
    } finally {
      setImporting(false);
    }
  };

  return {
    exporting,
    importing,
    importResults,
    handleExport,
    handleImport,
    setImportResults,
  };
};

